import { Box, Dialog, Grid } from "@mui/material";
import { useEffect, useState } from "react";

import * as yup from "yup";
import { Field, Form, Formik } from "formik";
import { string } from "yup";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import { AddProductCommand, Product } from "../../../../api/models/schema";
import { StandardTextBox } from "../../../../common/standardTextBox";
import { BasicErrorLabel } from "../../../../common/basicErrorLabel";

export interface FormValues {
  name: string;
  description: string;
  height: number;
  length: number;
  weight: number;
  min2Ft: number;
  max2Ft: number;
  trayType: string;
  thickness: number;
  mailPieceSize?: string;
  palletMinTrays: number;
  palletMaxTrays: number;
  scfValues?: string;
  postageAffixed?: string;
  northSouth?: string;
}

const validationSchema = yup.object({
  name: string().min(5, "too short").required("required"),
  height: yup.number().positive().required("required"),
  weight: yup.number().positive().required("required"),
  length: yup.number().positive().required("required"),
  thickness: yup.number().positive().required("required"),
  min2Ft: yup.number().positive().required("required"),
  max2Ft: yup.number().positive().required("required"),
  palletMinTrays: yup.number(),
  palletMaxTrays: yup.number(),

});

export interface AddProductFormProps {
  product: Product | undefined;
  errorMessage: string;
  open: boolean;
  isLoading: boolean;
  onClose: () => void;
  onUpdate: (client: AddProductCommand) => void;
}

export function AddProductForm(props: AddProductFormProps) {


  function update(values: FormValues) {
    const updateProduct: AddProductCommand = {
      productId: props.product?.productId,
      name: values.name,
      description: values.description,
      height: values.height,
      length: values.length,
      weight: values.weight,
      thickness: values.thickness,
      min2Ft: values.min2Ft,
      max2Ft: values.max2Ft,
      trayType: values.trayType,
      mailPieceSize: values.mailPieceSize,
      palletMinTrays: values.palletMinTrays,
      palletMaxTrays: values.palletMaxTrays,
      scfValues: values.scfValues,
      postageAffixed: values.postageAffixed,
      northSouth: values.northSouth,
    };

    props.onUpdate(updateProduct);
  }

  function handleClose() {
    props.onClose();
  }

  function displayForm() {
    return (
      <Box>
        <Dialog open={props.open}>
          <DialogTitle>Add Product</DialogTitle>
          <DialogContent>
            <Formik
              initialValues={
                props.product
                  ? {
                      trayType: props.product.trayType ?? '',
                      name: props.product.name,
                      description: props.product.description,
                      height: props.product.height,
                      weight: props.product.weight,
                      length: props.product.length,
                      max2Ft: props.product.max2Ft,
                      min2Ft: props.product.min2Ft,
                      thickness: props.product.thickness,
                      mailPieceSize: props.product.mailPieceSize,
                      palletMinTrays: props.product.palletMinTrays,
                      palletMaxTrays: props.product.palletMaxTrays,
                      scfValues: props.product.scfValues,
                      postageAffixed: props.product.postageAffixed,
                      northSouth: props.product.northSouth,

                    }
                  : {
                      name: "",
                      preSortClass: "",
                      description: "",
                      height: 0,
                      weight: 0,
                      length: 0,
                      max2Ft: 0,
                      trayType: "0MM",
                      min2Ft: 0,
                      thickness: 0,
                      mailPieceSize: "",
                      palletMinTrays: 0,
                      palletMaxTrays: 0,
                      scfValues: "",
                      postageAffixed: "",
                      northSouth: "",
                    }
              }
              validationSchema={validationSchema}
              onSubmit={(values, { setSubmitting }) => {
                setSubmitting(false);
                update(values);
              }}
            >
              {({ submitForm, isSubmitting }) => (
                <Form>
                  <br />

                  <Grid container spacing={2}>
                    <Grid item xs={3}>
                      Name
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="name"
                        label="Product Name"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Description
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="description"
                        label="Product Description"
                        width={375}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      Length (in)
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="length"
                        label="Length"
                        width={375}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      Height (in)
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="height"
                        label="Height"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Weight (oz)
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="weight"
                        label="Weight"
                        width={375}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      Thickness (in)
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="thickness"
                        label="Thickness"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Min2Ft
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="min2Ft"
                        label="Min2Ft"
                        width={375}
                      />
                    </Grid>
                    <Grid item xs={3}>
                      Max2Ft
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="max2Ft"
                        label="Max2Ft"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Tray Type
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="trayType"
                        label="Tray Type"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Mail Piece Size
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="mailPieceSize"
                        label="Mail Piece Size"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Pallet Min Trays
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="palletMinTrays"
                        label="Pallet Min Trays"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Pallet Max Trays
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="palletMaxTrays"
                        label="Pallet Max Trays"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      Postage Affixed
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="postageAffixed"
                        label="Postage Affixed"
                        width={375}
                      />
                    </Grid>

                    <Grid item xs={3}>
                      North South
                    </Grid>
                    <Grid item xs={9}>
                      <Field
                        component={StandardTextBox}
                        name="northSouth"
                        label="North South"
                        width={375}
                      />
                    </Grid>

                    <Grid
                      spacing={2}
                      item
                      container
                      xs={12}
                      justifyContent="flex-end"
                    >
                      {!props.isLoading && (
                        <Grid item>
                          <Button variant="contained" onClick={submitForm}>
                            Save
                          </Button>
                        </Grid>
                      )}
                      {props.isLoading && (
                        <Grid item>
                          <CircularProgress />
                        </Grid>
                      )}
                      <Grid item>
                        <Button onClick={handleClose}>Cancel</Button>
                      </Grid>
                    </Grid>

                    <Grid item xs={12}>
                      {props.errorMessage && (
                        <BasicErrorLabel message={props.errorMessage} />
                      )}
                    </Grid>
                  </Grid>
                </Form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>
      </Box>
    );
  }

  return <>{displayForm()}</>;
}
